import { useEffect, useState } from "react";
import { CalendarClock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export function WeeklyDigest({ onSent }) {
  const [d, setD] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/weekly-digest/status").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const send = async () => {
    setBusy("send");
    try { const { data } = await api.post("/admin/run-weekly-digest"); data.status === "sent" ? toast.success(`Weekly digest sent to ${data.to}`) : toast.error(data.error || `Digest ${data.status}`); load(); onSent?.(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const show = async () => {
    if (preview) { setPreview(null); return; }
    setBusy("prev");
    try { const { data } = await api.get("/admin/weekly-digest/preview"); setPreview(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  if (!d) return null;
  const last = d.last;
  const c = preview?.current;
  return (
    <div className="mb-3 rounded-2xl border border-indigo-200 bg-indigo-50/50 p-3 text-sm" data-testid="weekly-digest">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 font-bold"><CalendarClock className="h-4 w-4 text-indigo-600" /> Weekly owner digest</div>
          <div className="text-xs text-muted-foreground" data-testid="weekly-digest-schedule">
            {d.schedule} → <span className="font-mono">{d.to}</span> · signups, Plus conversions, revenue, churn, top features.
            {" "}Next: {new Date(d.next_run_at).toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            {last?.sent_at && <> · last sent {new Date(last.sent_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${last.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{last.status}</span></>}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={show} disabled={!!busy} data-testid="weekly-digest-preview-btn" className="rounded-full border border-indigo-300 bg-white px-4 py-2 text-xs font-bold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60">{busy === "prev" ? "Loading…" : preview ? "Hide preview" : "Preview numbers"}</button>
          <button onClick={send} disabled={!!busy} data-testid="weekly-digest-send-btn" className="rounded-full bg-indigo-600 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-60">{busy === "send" ? "Sending…" : "Send now"}</button>
        </div>
      </div>
      {c && (
        <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6" data-testid="weekly-digest-preview">
          {[["Signups", c.signups], ["Plus conv.", c.conversions], ["Revenue", `₹${c.revenue.toLocaleString("en-IN")}`], ["Churned", c.churned], ["WAU", c.active_users], ["MRR", `₹${preview.totals.mrr.toLocaleString("en-IN")}`]].map(([l, v]) => (
            <div key={l} className="rounded-xl bg-white px-3 py-2"><div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{l}</div><div className="font-display text-base font-bold">{v}</div></div>
          ))}
          <div className="col-span-3 text-[11px] text-muted-foreground sm:col-span-6">{preview.period.label} · top feature: <b>{preview.top_features[0]?.feature || "—"}</b> · vs last week: {preview.previous.signups} signups, {preview.previous.conversions} conv., ₹{preview.previous.revenue.toLocaleString("en-IN")}</div>
        </div>
      )}
    </div>
  );
}
